/**
 * Event Detail Query Hook
 *
 * Fetches event data with polling for real-time updates.
 */

import { useQuery, useQueryClient } from '@tanstack/react-query';
import { getEvent, type Event } from '../../api/events';
import { listEventDocuments, type EventDocument } from '../../api/documents';
import { queryKeys, POLL_INTERVALS } from '../../lib/queryClient';
import { getErrorMessage } from '../../api/client';
import { useOrganization } from '../../context/OrganizationContext';

interface UseEventOptions {
  /** Enable/disable automatic polling (default: true) */
  polling?: boolean;
  /** Custom polling interval in ms (default: 15000) */
  pollingInterval?: number;
}

export function useEvent(id: string | undefined, options: UseEventOptions = {}) {
  const { polling = true, pollingInterval = POLL_INTERVALS.EVENT_DETAIL } = options;
  const { currentOrg, isLoading: orgLoading } = useOrganization();

  const query = useQuery({
    queryKey: [...queryKeys.events.detail(id ?? ''), currentOrg?.orgSlug],
    queryFn: () => getEvent(id!),
    // Only run when id is provided and org context is ready
    enabled: !!id && !orgLoading && !!currentOrg,
    // Enable polling when specified
    refetchInterval: polling ? pollingInterval : false,
    // Stop polling when window is not visible
    refetchIntervalInBackground: false,
    // Consider data stale after half the poll interval
    staleTime: pollingInterval / 2,
    // Extract event from response
    select: (data) => data.event,
  });

  return {
    // Data
    event: query.data ?? null,

    // States - include org loading
    loading: orgLoading || query.isLoading,
    error: query.error ? getErrorMessage(query.error) : null,
    isRefetching: query.isRefetching,
    isStale: query.isStale,

    // Timestamps for UI
    dataUpdatedAt: query.dataUpdatedAt,

    // Actions
    refetch: query.refetch,
  };
}

/**
 * Hook for invalidating event data after mutations
 */
export function useInvalidateEvent() {
  const queryClient = useQueryClient();

  return (eventId: string) => {
    queryClient.invalidateQueries({ queryKey: queryKeys.events.detail(eventId) });
    // Also invalidate admin dashboard since event stats may have changed
    queryClient.invalidateQueries({ queryKey: queryKeys.adminDashboard });
  };
}

/**
 * Hook to update event data in cache without refetch
 */
export function useUpdateEventCache() {
  const queryClient = useQueryClient();

  return (eventId: string, event: Event) => {
    queryClient.setQueryData(queryKeys.events.detail(eventId), { event });
  };
}

// =============================================================================
// Document Hooks
// =============================================================================

interface UseEventDocumentsOptions {
  /** Enable/disable automatic polling (default: false for documents) */
  polling?: boolean;
  /** Custom polling interval in ms */
  pollingInterval?: number;
}

/**
 * Hook for fetching event documents
 */
export function useEventDocuments(
  eventId: string | undefined,
  options: UseEventDocumentsOptions = {}
) {
  const { polling = false, pollingInterval = POLL_INTERVALS.EVENT_DETAIL } = options;
  const { currentOrg, isLoading: orgLoading } = useOrganization();

  const query = useQuery({
    queryKey: [...queryKeys.documents.byEvent(eventId ?? ''), currentOrg?.orgSlug],
    queryFn: () => listEventDocuments(eventId!),
    enabled: !!eventId && !orgLoading && !!currentOrg,
    refetchInterval: polling ? pollingInterval : false,
    refetchIntervalInBackground: false,
    staleTime: pollingInterval / 2,
    select: (data) => data.documents,
  });

  return {
    documents: query.data ?? [] as EventDocument[],
    loading: orgLoading || query.isLoading,
    error: query.error ? getErrorMessage(query.error) : null,
    isRefetching: query.isRefetching,
    refetch: query.refetch,
  };
}

/**
 * Hook for invalidating document data after mutations
 */
export function useInvalidateDocuments() {
  const queryClient = useQueryClient();

  return (eventId: string) => {
    queryClient.invalidateQueries({ queryKey: queryKeys.documents.byEvent(eventId) });
  };
}
